import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X } from "lucide-react";

import { fetchFollowers, fetchFollowing } from "../../Services/SocialService";
import { formatInitials } from "../../utils/format";
import type { SocialUser } from "../../data/types";

type Props = {
  userId: string;
  mode: "followers" | "following";
  onClose: () => void;
};

export default function FollowListModal({ userId, mode, onClose }: Props) {
  const [users, setUsers] = useState<SocialUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setIsLoading(true);
    setError(null);

    const load = mode === "followers" ? fetchFollowers : fetchFollowing;

    load(userId)
      .then((result) => {
        if (alive) setUsers(result);
      })
      .catch(() => {
        if (alive) setError("Could not load the list.");
      })
      .finally(() => {
        if (alive) setIsLoading(false);
      });

    return () => {
      alive = false;
    };
  }, [userId, mode]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  return (
    <div
      className="follow-modal-backdrop"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div className="follow-modal" onClick={(e) => e.stopPropagation()}>
        <div className="follow-modal-header">
          <h3>{mode === "followers" ? "Followers" : "Following"}</h3>

          <button type="button" onClick={onClose} aria-label="Close">
            <X size={18} strokeWidth={2} />
          </button>
        </div>

        <div className="follow-modal-body">
          {isLoading && <p className="follow-modal-status">Loading…</p>}
          {error && <p className="follow-modal-status">{error}</p>}

          {!isLoading && !error && users.length === 0 && (
            <p className="follow-modal-status">Nobody here yet.</p>
          )}

          {users.map((user) => (
            <Link
              key={user.id}
              to={`/profile/${user.id}`}
              className="follow-modal-row"
              onClick={onClose}
            >
              <div className="artist-avatar">
                {user.avatarUrl ? (
                  <img src={user.avatarUrl} alt="" />
                ) : (
                  <span>{formatInitials(user.displayName)}</span>
                )}
              </div>

              <div className="follow-modal-row-text">
                <strong>{user.displayName}</strong>
                <small>
                  {user.isSelf
                    ? "This is you"
                    : user.followState === "following"
                    ? "Following"
                    : user.followState === "requested"
                    ? "Request sent"
                    : "Not following"}
                </small>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
